import compact from 'lodash/compact'
import filter from 'lodash/filter'
import find from 'lodash/find'
import first from 'lodash/first'
import flatten from 'lodash/flatten'
import forEach from 'lodash/forEach'
import get from 'lodash/get'
import indexOf from 'lodash/indexOf'
import join from 'lodash/join'
import map from 'lodash/map'
import orderBy from 'lodash/orderBy'
import reduce from 'lodash/reduce'
import sortBy from 'lodash/sortBy'
import toNumber from 'lodash/toNumber'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'

import { PersonalisationModalTypes, Personalization, personalizationTranslationKeys, ProductPersonalization } from '@Components/Personalization/Modals/Shared'
import { RelatedProduct } from '@Components/Product/RelatedProducts'
import { ProductTileProps } from '@Components/Product/Tile'
import { Product as ProductMParticle } from '@Events/ECommerce'
import {
  Attribute,
  BaseAttribute,
  IWithMoney,
  LinkedProductsFragment,
  Maybe,
  MoneyAttribute,
  Product,
  ProductAttribute,
  ProductContentFragment,
  ProductVariant,
  ProductWithQuantity,
  Scalars,
} from '@Generated/graphql'
import { AlgoliaProductV2 } from '@Globals/Algolia/Types'
import { Product as ProductGTM } from '@Globals/Google/DataLayer'

export interface LinkedProduct {
  image: string
  productID: string
  title: string
  type: string
  url: string
}

const isBaseAttribute = (attribute: Attribute | undefined):
  attribute is BaseAttribute => attribute?.__typename === 'BaseAttribute'

// TODO: Remove eslint-disable when used
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isProductAttribute = (attribute: Attribute | undefined):
  attribute is ProductAttribute => attribute?.__typename === 'ProductAttribute'

// TODO: Remove eslint-disable when used
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isMoneyAttribute = (attribute: Attribute | undefined):
  attribute is MoneyAttribute => attribute?.__typename === 'MoneyAttribute'

export const getBaseAttribute = <T extends Scalars['JSON']>(attributes: Attribute[], key: string): Maybe<T> => {
  const attribute = attributes.find(x => x.key === key)
  return isBaseAttribute(attribute) ? attribute.value as T : null
}

const isAlgoliaProduct = (product: Product | AlgoliaProductV2): product is AlgoliaProductV2 => (product as AlgoliaProductV2).objectID !== undefined

export const convertToGTM = (product: Product | AlgoliaProductV2): ProductGTM => {
  if (isAlgoliaProduct(product)) {
    return {
      categoryIDs: product.categoryIDs || [],
      id: product.objectID,
      // @ts-ignore
      localPrice: product.displayPrice?.discount?.plain || product.displayPrice?.plain,
      name: product.title || '',
    }
  }

  return {
    categoryIDs: product.categoryIDs || [],
    id: product.id,
    // @ts-ignore
    localPrice: toNumber(product.masterVariant?.basePrice?.discount?.plain) || toNumber(product.masterVariant?.basePrice?.plain) || 0,
    name: product.name || '',
  }
}

export const convertToMParticle = (product: Product | AlgoliaProductV2, quantity = 0, personalizationType = ''): ProductMParticle => {
  if (isAlgoliaProduct(product)) {
    const price = toNumber(product.displayPrice?.discount?.plain) || toNumber(product.displayPrice?.plain) || 0

    return {
      Attributes: {
        // TODO: CATEGORIES need to be adjusted accordingly from algolia.
        categories: '',
        category_ids: `-${join(product.categoryIDs, '-')}-`,
        category_main: '',
        category_sub: '',
        is_on_sale: (product.displayPrice?.discount?.value || 0) > 0,
        // @ts-ignore
        local_price: price,
        personalisation_type: personalizationType,
      },
      Brand: product.brand || '',
      Category: '',
      CouponCode: '',
      Name: '',
      Position: 0,
      Price: price,
      Sku: product.objectID || '',
      Quantity: quantity,
      TotalAmount: price * quantity,
      Variant: '',
    }
  }

  const categories: string[] = []

  // @ts-ignore
  const categoryMain = product.primaryCategories?.[0]?.name || ''
  if (categoryMain) {
    categories.push(categoryMain)
  }

  // @ts-ignore
  const categorySub = product.primaryCategories?.[1]?.name || ''
  if (categorySub) {
    categories.push(categorySub)
  }

  // @ts-ignore
  const price = toNumber(product.masterVariant?.basePrice?.discount?.plain) || toNumber(product.masterVariant?.basePrice?.plain) || 0

  return {
    Attributes: {
      // TODO: add profit_amount, has_video, is_new, and customer_order_count for purchase events
      categories: join(categories, '/'),
      category_ids: `-${join(product.categoryIDs, '-')}-`,
      category_main: categoryMain,
      category_sub: categorySub,
      is_on_sale: (product.masterVariant?.price?.discount?.value || 0) > 0,
      // @ts-ignore
      local_price: toNumber(product.masterVariant?.price?.discount?.plain) || toNumber(product.masterVariant?.price?.plain) || 0,
      personalisation_type: personalizationType,
    },
    Brand: product.brand?.title || '',
    Category: '',
    CouponCode: '',
    Name: '',
    Position: 0,
    Price: price,
    Sku: product.id || '',
    Quantity: quantity,
    TotalAmount: price * quantity,
    Variant: '',
  }
}

export const cmsProductToGridProduct = (
  product: Product,
  greyOverlay = false,
): ProductTileProps | undefined => {
  // TODO: Consider checking on more attributes
  // When no price is available for a product, there's no need to render it
  if (!product?.masterVariant?.price?.format) {
    return undefined
  }

  const normalPrice = product.masterVariant.price
  const price = product.masterVariant.price.discount ?? normalPrice
  // TODO: Get this from graph instead
  const discountPercentage = product.masterVariant.price.discount?.percent ?? 0
  // TODO: Refactor to use query-specific product data, to make primaryAsset available directly.
  // Currently we use type hints that does not match what we query, which hinders a lot of what graph gives us.
  // No custom resolvers on a product is available currently.
  const primaryImage = find(
    product.assets,
    asset => indexOf(asset.tags || [], 'primary') !== -1,
  )?.url || product.assets?.[0]?.url

  const isInStock = product.availability?.isInStock
  const quantity = product.availability?.quantity || 0
  const isOutOfStock = !isInStock && quantity <= 0

  const isNew = getBaseAttribute<boolean>(product.masterVariant.attributes || [], 'isNew') || false

  const discountContext = product.masterVariant?.price?.discount?.code?.context
  return {
    beforePrice: (product.masterVariant.price.discount && normalPrice.format) || undefined,
    brandTitle: product.brand?.title || '',
    discount: {
      percentage: discountPercentage,
      ...discountContext && { context: discountContext },
    },
    greyOverlay,
    id: product.id,
    isNew,
    isOutOfStock,
    gtmProduct: convertToGTM(product),
    mParticleProduct: convertToMParticle(product, 1),
    price: price.format || '',
    primaryImage,
    title: product.name || '',
    productType: product.type || '',
    url: product.url || '',
  }
}

export const buildLinkedProductList = (product?: Product): LinkedProduct[] => flatten(
  map(product?.linkedProducts as LinkedProductsFragment[], ({ products, type }) => map(
    // @ts-ignore - because restock.value doesn't exist in the types.
    filter(products, ({
      availability,
      masterVariant,
    }) => availability?.isInStock || (masterVariant?.restock as BaseAttribute)?.value !== 'disabled'),
    ({ id = '', name = '', primaryAsset, url }): LinkedProduct => ({
      productID: id || '',
      type: type || '',
      url: url || '',
      image: primaryAsset?.url || '',
      title: name || '',
    }),
  )),
)

export const getLinkedProductIDs = (product?: Product): string[] => uniq(flatten(
  map(product?.linkedProducts as LinkedProductsFragment[], ({ products }) => map(
    products,
    ({ id }) => (id),
  )),
))

// @ts-ignore - "product.bundle" doesn't exist in the auto-generated types, but it's in the query.
export const getBundledProductList = (product?: Product): ProductWithQuantity[] => (product?.masterVariant?.bundleContent?.products || [])
// @ts-ignore - ditto.
export const getBundlePriceDifference = (product?: Product): IWithMoney | undefined => (product?.masterVariant?.bundlePrice?.difference)

export const getGalleryAssetsFromProduct = (product?: Product): Product['assets'] => filter(
  product?.assets,
  asset => asset.tags?.includes('gallery'),
) as Product['assets']

export const getFirstVideoIndexInAssets = (assets?: Product['assets']) => assets?.findIndex(asset => asset.tags?.includes('video'))

export type CrossSellProduct =
  Pick<Product, 'name' | 'url' | 'id' | 'masterVariant'>
  & Pick<ProductContentFragment, 'primaryAsset'>

// @ts-ignore - ditto.
export const getCrossSellsForProduct = (product?: Product): CrossSellProduct[] => (product?.masterVariant?.crossSells?.products || [])

type GetAvailableVariantsProps = {
  includeVariantsThatWillNeverRestock?: boolean
  locale?: string
  product?: Product
}
export type SizeProps = {
  available?: boolean
  id: ProductVariant['id']
  key: string
  label: string
}

type FilteredVariantsReturnFormat = {
  availableVariants: SizeProps[]
  variantsInTotal: number
}

export const getAvailableVariantsForProduct = (
  {
    includeVariantsThatWillNeverRestock,
    locale,
    product,
  }: GetAvailableVariantsProps,
): FilteredVariantsReturnFormat => {
  if (!product) {
    return {
      availableVariants: [],
      variantsInTotal: 0,
    }
  }

  let variantsInTotal = 0
  const availableVariants = reduce<ProductVariant, SizeProps[]>(
    (product.variants || []) as ProductVariant[],
    (memo, item) => {
      const size: Record<string, any> | undefined = find(item.attributes, ['key', 'size'])
      if (!size) {
        return memo
      }

      const available = item.availability?.isInStock || false
      variantsInTotal += 1

      // If the product isn't available, we need to know if it ever will be again. If it isn't
      // due a restock at any time, there's no reason to show it to the user. They'll just be sad.
      // However, we might want to include the "dead" variants in certain cases, which is why we'll
      // do a double-check here.
      if (!available && !includeVariantsThatWillNeverRestock) {
        const restock: Record<string, any> | undefined = find(item.attributes, ['key', 'restock'])

        if (restock?.value?.key !== 'enabled') {
          return memo
        }
      }

      return [
        ...memo,
        {
          available,
          id: item.id,
          key: size?.value?.key || '',
          label: size?.value?.label?.[locale || 'en-GB'] || size?.value?.key || '',
        },
      ]
    },
    [],
  )

  return {
    availableVariants,
    variantsInTotal,
  }
}

export const getPersonalizationsFromProduct = ({ masterVariant }: Product): ProductPersonalization[] => (
  // @ts-ignore
  masterVariant?.personalizations?.products as ProductPersonalization[] || []
)

export const getPersonalizationTypes = (items: ProductPersonalization[]): string[] => compact(uniq(map(items, 'masterVariant.type.value.key')))

const sortProductPersonalizations = (personalizations: ProductPersonalization[]) => {
  const preferredOrder = ['uv-print', 'engrave', 'deboss']
  return sortBy(personalizations, p => preferredOrder.indexOf(p.masterVariant.type.value.key))
}

export const getUniqProductPersonalizations = (product: Product): ProductPersonalization[] => {
  const personalizations = getPersonalizationsFromProduct(product)
  return sortProductPersonalizations(uniqBy(personalizations, p => p.masterVariant.type.value.key))
}

export const getPersonalization = (item: ProductPersonalization) => ({
  id: item.masterVariant?.legacyID?.value || '',
  price: item.masterVariant?.price?.format || '',
  image: item.primaryAsset?.url || '',
  placement: item.masterVariant?.shortName?.value || {},
  maxCharacters: item.masterVariant?.maxCharacters?.value,
  type: item.masterVariant.type.value.key,
})

export const getPersonalizations = (items: ProductPersonalization[]): Personalization[] => map(items, getPersonalization)

export const getPersonalizationsByType = (items: ProductPersonalization[], type: PersonalisationModalTypes): Personalization[] => {
  const filteredPersonalisations = filter(items, personalisation => personalisation.masterVariant?.type?.value?.key === type)
  return getPersonalizations(filteredPersonalisations)
}

export const getProductTilesFromRelatedProducts = (products?: RelatedProduct[], allowMoneyShot?: boolean): (ProductTileProps & { key: string })[] => map(
  products,
  (product) => {
    const price = product.masterVariant?.price?.format
    const discount = product.masterVariant?.price?.discount?.format
    return ({
      key: product.id,
      beforePrice: (discount && price) || undefined,
      brandTitle: product.brand?.title || '',
      discount: {
        context: product.masterVariant?.price?.discount?.code?.context || '',
        percentage: product.masterVariant?.price?.discount?.percent || 0,
      },
      greyOverlay: true,
      id: product.id,
      isNew: get(product.masterVariant, 'isNew')?.value ?? false,
      gtmProduct: convertToGTM(product),
      mParticleProduct: convertToMParticle(product, 1),
      price: discount || price || '',
      primaryImage: (allowMoneyShot && product.moneyShotAsset?.url) || product.primaryAsset?.url,
      productType: product.type || '',
      title: product.name || '',
      url: product.url || '',
    })
  },
)

export const algoliaProductToProductTile = (products: AlgoliaProductV2[], storeURL): ProductTileProps[] => map(products, product => ({
  beforePrice: product.displayPrice?.discount && product.displayPrice?.format,
  brandTitle: product.brand,
  discount: product.displayPrice?.discount && {
    context: product.displayPrice.discount.code?.context || '',
    percentage: product.displayPrice?.discount?.percent || 0,
  },
  gtmProduct: convertToGTM(product),
  id: product.objectID,
  isNew: product.isNew,
  isOutOfStock: !product.isInStock,
  mParticleProduct: convertToMParticle(product),
  price: product.displayPrice?.format,
  primaryImage: product.primaryAsset.url,
  hoverImage: product.hoverAsset.url,
  sourceID: 'inherit',
  title: product.title,
  url: storeURL(product.slug),
}))

// These are the product types we'll be allowing size information for.
const productTypeSizeMapping = {
  // This is the "type" on the product
  bracelets: {
    // This is the attribute containing the sizes we'll be rendering
    attributeName: 'braceletFits',
    // This is the name of the key inside the size attributes we'll be looking for.
    sizeKey: 'circumference',
    fitKey: 'fit',
  },
}

export const getSizeInformation = (locale: string, product?: Product): ({ data: ({ fit: string, size: string })[], key: string })[] | null => {
  if (!product) {
    return null
  }

  const { type = '' } = product
  const currentMap = productTypeSizeMapping[type as string]
  if (!currentMap) {
    return null
  }

  return map(product.variants || [], (variant) => {
    const sizeKeyData = find(variant?.attributes, ['key', 'size']) as unknown as (BaseAttribute | undefined)
    const sizeInfoData = find(variant?.attributes, ['key', currentMap.attributeName]) as unknown as (BaseAttribute | undefined)

    const sizeKey = (sizeKeyData?.value as Record<string, string>)?.label[locale]
    const sizeData = map(sizeInfoData?.value as BaseAttribute[], (sizeBlock) => {
      const size = ((find(sizeBlock, ['name', currentMap.sizeKey]) as any)?.value as Record<string, any>)?.label[locale]
      const fit = ((find(sizeBlock, ['name', currentMap.fitKey]) as any)?.value?.label[locale])

      return { size, fit }
    })
    return { key: sizeKey, data: sizeData }
  })
}

type ProductInfo = {
  addedFromCartModal: boolean
  addedProductIDsWithPersonalizationType: any
  addedProductsAlreadyInCart: any
  productID: string
}

const checkEqualProductID = (productID, itemPersonalization) => productID.toString() === itemPersonalization.product.id
const checkEqualTypes = (item, type) => item.type === type

function checkForPersonalizations(productInfo: ProductInfo, type: string) {
  const {
    addedFromCartModal,
    addedProductIDsWithPersonalizationType,
    addedProductsAlreadyInCart,
    productID,
  } = productInfo
  const addedProduct: { personalization: { type: string } | null } | null = first(addedProductsAlreadyInCart) ?? null
  let hasBeenRecentlyAdded = false
  let hasPersonalizationAdded = false

  if (addedProductIDsWithPersonalizationType && addedProductIDsWithPersonalizationType.length > 0) {
    forEach(addedProductIDsWithPersonalizationType, (itemPersonalization) => {
      const isProductPersonalizationIDEqual = checkEqualProductID(productID, itemPersonalization)
      if (isProductPersonalizationIDEqual) {
        hasPersonalizationAdded = true
        if (addedFromCartModal && checkEqualTypes(itemPersonalization, type)) {
          hasBeenRecentlyAdded = true
        }
      }
    })
  } else if (addedProduct && addedProduct.personalization?.type) {
    hasPersonalizationAdded = true
  }
  return { hasBeenRecentlyAdded, hasPersonalizationAdded }
}

interface PersonalizationGeneratorProps {
  hasBeenRecentlyAdded: boolean
  id: string | number
  image: string
  price: string
  title: string
  type: string
}

export const generatePersonalizationData = (personalizations: ProductPersonalization[], productInfo: ProductInfo): (PersonalizationGeneratorProps | null)[] => {
  if (!personalizations) {
    return []
  }

  const orderedPersonalizations = orderBy(uniqBy(
    getPersonalizations(personalizations),
    (personalization => personalization.type),
  ), ['type'], 'desc')

  return map(orderedPersonalizations, (personalization) => {
    const { id, image, price, type } = personalization

    const displayImage = type === 'uv-print'
      ? 'https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/blt92740812a0dfbdf2/uv-image-child-in-watch.png'
      : image

    const {
      hasBeenRecentlyAdded,
      hasPersonalizationAdded,
    } = checkForPersonalizations(productInfo, type)

    if (hasPersonalizationAdded && !hasBeenRecentlyAdded) {
      return null
    }

    return {
      id,
      price,
      image: displayImage,
      title: personalizationTranslationKeys[`${type}-button`],
      hasBeenRecentlyAdded,
      type,
    }
  })
}

